import L from "leaflet";

L.DomUtil = L.extend(L.DomUtil, {
  getMatrixString(m) {
    const is3d = L.Browser.webkit3d || L.Browser.gecko3d || L.Browser.ie3d;

    /*
     * Since matrix3d takes a 4*4 matrix, we add in an empty row and column, which act as the identity on the z-axis.
     * See:
     *     http://franklinta.com/2014/09/08/computing-css-matrix3d-transforms/
     *     https://developer.mozilla.org/en-US/docs/Web/CSS/transform-function#M.C3.B6bius'_homogeneous_coordinates_in_projective_geometry
     */
    const matrix = [
      m[0],
      m[3],
      0,
      m[6],
      m[1],
      m[4],
      0,
      m[7],
      0,
      0,
      1,
      0,
      m[2],
      m[5],
      0,
      m[8],
    ];

    const str = is3d ? `matrix3d(${matrix.join(",")})` : "";

    if (!is3d) {
      console.log(
        "Your browser must support 3D CSS transforms in order to use DistortableImageOverlay."
      );
    }

    return str;
  },

  getRotateString(angle, units) {
    const is3d = L.Browser.webkit3d || L.Browser.gecko3d || L.Browser.ie3d;
    const open = `rotate${is3d ? "3d" : ""}(`;
    const rotateString = (is3d ? "0, 0, 1, " : "") + angle + units;

    return `${open + rotateString})`;
  },
});

L.Map.include({
  _newLayerPointToLatLng(point, newZoom, newCenter) {
    const topLeft = L.Map.prototype._getNewTopLeftPoint
      .call(this, newCenter, newZoom)
      .add(L.Map.prototype._getMapPanePos.call(this));
    return this.unproject(point.add(topLeft), newZoom);
  },
});
L.MatrixUtil = {
  // Compute the adjugate of m
  adj(m) {
    return [
      m[4] * m[8] - m[5] * m[7],
      m[2] * m[7] - m[1] * m[8],
      m[1] * m[5] - m[2] * m[4],
      m[5] * m[6] - m[3] * m[8],
      m[0] * m[8] - m[2] * m[6],
      m[2] * m[3] - m[0] * m[5],
      m[3] * m[7] - m[4] * m[6],
      m[1] * m[6] - m[0] * m[7],
      m[0] * m[4] - m[1] * m[3],
    ];
  },

  // multiply two 3*3 matrices
  multmm(a, b) {
    const c = [];
    let i;

    for (i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        let cij = 0;
        for (let k = 0; k < 3; k++) {
          cij += a[3 * i + k] * b[3 * k + j];
        }
        c[3 * i + j] = cij;
      }
    }
    return c;
  },

  // multiply a 3*3 matrix and a 3-vector
  multmv(m, v) {
    return [
      m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
      m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
      m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
    ];
  },

  // multiply a scalar and a 3*3 matrix
  multsm(s, m) {
    const matrix = [];

    for (let i = 0, l = m.length; i < l; i++) {
      matrix.push(s * m[i]);
    }

    return matrix;
  },

  basisToPoints(x1, y1, x2, y2, x3, y3, x4, y4) {
    const m = [x1, x2, x3, y1, y2, y3, 1, 1, 1];
    const v = L.MatrixUtil.multmv(L.MatrixUtil.adj(m), [x4, y4, 1]);

    return L.MatrixUtil.multmm(m, [v[0], 0, 0, 0, v[1], 0, 0, 0, v[2]]);
  },

  project(m, x, y) {
    const v = L.MatrixUtil.multmv(m, [x, y, 1]);
    return [v[0] / v[2], v[1] / v[2]];
  },

  general2DProjection(
    x1s,
    y1s,
    x1d,
    y1d,
    x2s,
    y2s,
    x2d,
    y2d,
    x3s,
    y3s,
    x3d,
    y3d,
    x4s,
    y4s,
    x4d,
    y4d
  ) {
    const s = L.MatrixUtil.basisToPoints(
      x1s,
      y1s,
      x2s,
      y2s,
      x3s,
      y3s,
      x4s,
      y4s
    );
    const d = L.MatrixUtil.basisToPoints(
      x1d,
      y1d,
      x2d,
      y2d,
      x3d,
      y3d,
      x4d,
      y4d
    );
    const m = L.MatrixUtil.multmm(d, L.MatrixUtil.adj(s));

    /*
     *	Normalize to the unique matrix with m[8] == 1.
     * 	See: http://franklinta.com/2014/09/08/computing-css-matrix3d-transforms/
     */
    return L.MatrixUtil.multsm(1 / m[8], m);
  },
};
L.EditHandle = L.Marker.extend({
  initialize(overlay, corner, options) {
    let markerOptions;
    const latlng = overlay._corners[corner];

    L.setOptions(this, options);

    this._handled = overlay;
    this._corner = corner;

    markerOptions = {
      draggable: true,
      zIndexOffset: 10,
    };

    if (options && options.hasOwnProperty("draggable")) {
      markerOptions.draggable = options.draggable;
    }

    L.Marker.prototype.initialize.call(this, latlng, markerOptions);
  },

  onAdd(map) {
    L.Marker.prototype.onAdd.call(this, map);
    this._bindListeners();

    this.updateHandle();
  },

  onRemove(map) {
    this._unbindListeners();
    L.Marker.prototype.onRemove.call(this, map);
  },

  _onHandleDragStart() {
    this._handled.fire("editstart");
  },

  _onHandleDragEnd() {
    this._fireEdit();
  },

  _fireEdit() {
    this._handled.edited = true;
    this._handled.fire("edit");
  },

  _bindListeners() {
    this.on(
      {
        dragstart: this._onHandleDragStart,
        drag: this._onHandleDrag,
        dragend: this._onHandleDragEnd,
      },
      this
    );

    this._handled._map.on("zoomend", this.updateHandle, this);

    this._handled.on("update", this.updateHandle, this);
  },

  _unbindListeners() {
    this.off(
      {
        dragstart: this._onHandleDragStart,
        drag: this._onHandleDrag,
        dragend: this._onHandleDragEnd,
      },
      this
    );

    this._handled._map.off("zoomend", this.updateHandle, this);
    this._handled.off("update", this.updateHandle, this);
  },
});

L.LockHandle = L.EditHandle.extend({
  options: {
    TYPE: "lock",
    icon: new L.Icon({
      iconUrl:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAD8SURBVO3BPU7CYAAA0AdfjIcQlRCQBG7C3gk2uIPG2RC3Dk16Gz0FTO1WZs/gwGCMP/2+xsSl7+n1er1Iz9LtRQjaPeMeO+TinLDCJV78YqjdA04YodKuxhUaPGoRxMmxwRQZSt87Yo4KExGCeAUyLLFB4bMacxywEClIU2KDKXbInTUYo8JCgoFuGoxQO5uiwY1EA91VmDqrcKeDoX8WdNNgjApvmGGLXKIgXY0xGkxQYItrrFFIEKQ5Yo4KEx9yrDFDhlKkIF6NOQ5Y+KpAhiXWKEQI4pxwiwoLPyuxwQw75FoE7fZYocFEuwI7jHCBV39gL92TXq/Xi/AOcmczZmaIMScAAAAASUVORK5CYII=",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    }),
  },

  /* cannot be dragged */
  _onHandleDrag() {},

  updateHandle() {
    this.setLatLng(this._handled._corners[this._corner]);
  },
});

L.DistortHandle = L.EditHandle.extend({
  options: {
    TYPE: "distort",
    icon: new L.Icon({
      iconUrl:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAChSURBVO3BIU4DURgGwNkvL2B6AkQTLBqP4QCoSm7DDXoBLBZHDbfgICAIZjEV3YTn9uVHdMZZtcnCfI13bIzxg0emg6Nm6QVbYz3jylEsXRrvwommb49X67jFkz80fR9Mb1YxTzqiWBSLYlEsikWxKBbFolgUi2JRLIpFsSgWxaJY03fHHOu40dH07bAzWCx9Ge/TiWbpHgdsjPGNB2f/yS+7xRCyiiZPJQAAAABJRU5ErkJggg==",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    }),
  },

  updateHandle() {
    this.setLatLng(this._handled._corners[this._corner]);
  },

  _onHandleDrag() {
    this._handled._updateCorner(this._corner, this.getLatLng());

    this._handled.fire("update");
  },
});

L.RotateHandle = L.EditHandle.extend({
  options: {
    TYPE: "rotate",
    icon: new L.Icon({
      iconUrl:
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAklEQVR4AewaftIAAAHiSURBVMXBa3HbShgA0PMp/1sCCo8oCEpgTaCXgIXAJiDzyCJoAUTm4UVQAns1Y8+snWnTvJyeE16hkjDgDrfoNTMKcpC9UPiLSo8JyetkjEHxjPCMyoS199kFoz8Iv1HpMaN3qWDCHoegOKkkRwnJpRmroHgiPFEZ8IBekzEGxQtUEhKSS/fB7Ew4U+lxcGkVZG9QWWPSFAxBcdK59KApuA+yNwp2uEdx1GN25sZJZULSfAtm77SlbNjju6MvG75u+WHRWVR6rDVjMPsgwYyVZl3pLTpHkyYHOx8syMiayaJzlDTZ9YyaZNFVkiYH2ZUEBcVJJXVImuz6Js3Qofe59pq7DoOTILu+g+a288mCouk7/1iH4qTS+2QdDppbV1ZJmrnDXnPnc5UOs2Z0fUmTuyBr+krvSioJyUmQO0dZM7mepMkWnaNRkyrJB6uskTSjxY3Fll8bvmJwlDb83FJ8gMqAB80uyBY3Trb82PAfvjj6vuHnluIdKgMeNXOwctK5NKBoHitrb1RJeHRp5Ux4ojLg0aWMHGQvUOkxIWkKVsHsTPiNSo8HDC5lZIsgO6n0uMUdRvQuFQxB8UR4RmXC2vvsgtEfhL+o9JiQvE7GGBTPCK9QSUjoMWgKDthjDrIX+h/k0I7gth6N5gAAAABJRU5ErkJggg==",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    }),
  },

  _onHandleDrag() {
    const overlay = this._handled;
    const formerLatLng = this._handled._corners[this._corner];
    const newLatLng = this.getLatLng();

    const angle = this._calculateAngle(formerLatLng, newLatLng);
    // scale = this._calculateScalingFactor(formerLatLng, newLatLng);

    overlay.editing._rotateBy(angle);
    // overlay.editing._scaleBy(scale);

    overlay.fire("update");
  },

  updateHandle() {
    this.setLatLng(this._handled._corners[this._corner]);
  },

  /* Takes two latlngs and calculates the angle between them. */
  _calculateAngle(latlngA, latlngB) {
    const map = this._handled._map;

    const centerPoint = map.latLngToLayerPoint(this._handled.getCenter());
    const formerPoint = map.latLngToLayerPoint(latlngA);
    const newPoint = map.latLngToLayerPoint(latlngB);

    const initialAngle = Math.atan2(
      centerPoint.y - formerPoint.y,
      centerPoint.x - formerPoint.x
    );
    const newAngle = Math.atan2(
      centerPoint.y - newPoint.y,
      centerPoint.x - newPoint.x
    );

    return newAngle - initialAngle;
  },

  /* Takes two latlngs and calculates the scaling difference. */
  _calculateScalingFactor(latlngA, latlngB) {
    const map = this._handled._map;

    const centerPoint = map.latLngToLayerPoint(this._handled.getCenter());
    const formerPoint = map.latLngToLayerPoint(latlngA);
    const newPoint = map.latLngToLayerPoint(latlngB);

    const formerRadiusSquared = this._d2(centerPoint, formerPoint);
    const newRadiusSquared = this._d2(centerPoint, newPoint);

    return Math.sqrt(newRadiusSquared / formerRadiusSquared);
  },

  /* Distance between two points in cartesian space, squared (distance formula). */
  _d2(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;

    return Math.pow(dx, 2) + Math.pow(dy, 2);
  },
});

// Start Scale
L.ScaleHandle = L.EditHandle.extend({
  options: {
    TYPE: "scale",
    icon: new L.Icon({
      iconUrl:
        "data:image/svg+xml;base64,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",
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    }),
  },

  _onHandleDrag() {
    const overlay = this._handled;
    const formerLatLng = this._handled._corners[this._corner];
    const newLatLng = this.getLatLng();
    const angle = this._calculateAngle(formerLatLng, newLatLng);
    const scale = this._calculateScalingFactor(formerLatLng, newLatLng);

    // overlay.editing._rotateBy(angle);
    overlay.editing._scaleBy(scale);

    overlay.fire("update");
  },

  updateHandle() {
    this.setLatLng(this._handled._corners[this._corner]);
  },

  /* Takes two latlngs and calculates the angle between them. */
  _calculateAngle(latlngA, latlngB) {
    const map = this._handled._map;

    const centerPoint = map.latLngToLayerPoint(this._handled.getCenter());
    const formerPoint = map.latLngToLayerPoint(latlngA);
    const newPoint = map.latLngToLayerPoint(latlngB);

    const initialAngle = Math.atan2(
      centerPoint.y - formerPoint.y,
      centerPoint.x - formerPoint.x
    );
    const newAngle = Math.atan2(
      centerPoint.y - newPoint.y,
      centerPoint.x - newPoint.x
    );

    return newAngle - initialAngle;
  },

  /* Takes two latlngs and calculates the scaling difference. */
  _calculateScalingFactor(latlngA, latlngB) {
    const map = this._handled._map;

    const centerPoint = map.latLngToLayerPoint(this._handled.getCenter());
    const formerPoint = map.latLngToLayerPoint(latlngA);
    const newPoint = map.latLngToLayerPoint(latlngB);

    const formerRadiusSquared = this._d2(centerPoint, formerPoint);
    const newRadiusSquared = this._d2(centerPoint, newPoint);

    return Math.sqrt(newRadiusSquared / formerRadiusSquared);
  },

  /* Distance between two points in cartesian space, squared (distance formula). */
  _d2(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;

    return Math.pow(dx, 2) + Math.pow(dy, 2);
  },
});

// End Scale

L.DistortableImageOverlay = L.ImageOverlay.extend({
  include: L.Mixin.Events,

  options: {
    alt: "",
    height: 200,
    crossOrigin: false,
  },

  initialize(url, options) {
    this._url = url;
    this._rotation = this.options.rotation;

    L.setOptions(this, options);
  },

  onAdd(map) {
    /* Copied from L.ImageOverlay */
    this._map = map;

    if (!this._image) {
      this._initImage();
    }
    if (!this._events) {
      this._initEvents();
    }

    map._panes.overlayPane.appendChild(this._image);

    map.on("viewreset", this._reset, this);
    /* End copied from L.ImageOverlay */

    /* Use provided corners if available */
    if (this.options.corners) {
      this._corners = this.options.corners;
      if (map.options.zoomAnimation && L.Browser.any3d) {
        map.on("zoomanim", this._animateZoom, this);
      }

      /* This reset happens before image load; it allows
       * us to place the image on the map earlier with
       * "guessed" dimensions. */
      this._reset();
    }

    /* Have to wait for the image to load because
     * we need to access its width and height. */
    L.DomEvent.on(
      this._image,
      "load",
      function () {
        this._initImageDimensions();
        this._reset();
        /* Initialize default corners if not already set */
        if (!this._corners) {
          if (map.options.zoomAnimation && L.Browser.any3d) {
            map.on("zoomanim", this._animateZoom, this);
          }
        }
      },
      this
    );

    this.fire("add");
  },

  onRemove(map) {
    this.fire("remove");

    L.ImageOverlay.prototype.onRemove.call(this, map);
  },

  _initImage() {
    L.ImageOverlay.prototype._initImage.call(this);

    L.extend(this._image, {
      alt: this.options.alt,
    });
  },

  _initImageDimensions() {
    const map = this._map;

    if (map) {
      const originalImageWidth = L.DomUtil.getStyle(this._image, "width");
      const originalImageHeight = L.DomUtil.getStyle(this._image, "height");

      const aspectRatio =
        parseInt(originalImageWidth) / parseInt(originalImageHeight);

      const imageHeight = this.options.height;
      const imageWidth = parseInt(aspectRatio * imageHeight);

      const center = map.latLngToContainerPoint(map.getCenter());
      const offset = new L.Point(imageWidth, imageHeight).divideBy(2);

      if (this.options.corners) {
        this._corners = this.options.corners;
      } else {
        this._corners = [
          map.containerPointToLatLng(center.subtract(offset)),
          map.containerPointToLatLng(
            center.add(new L.Point(offset.x, -offset.y))
          ),
          map.containerPointToLatLng(
            center.add(new L.Point(-offset.x, offset.y))
          ),
          map.containerPointToLatLng(center.add(offset)),
        ];
      }
    }
  },

  _initEvents() {
    this._events = ["click"];

    for (let i = 0, l = this._events.length; i < l; i++) {
      L.DomEvent.on(this._image, this._events[i], this._fireMouseEvent, this);
    }
  },

  /* See src/layer/vector/Path.SVG.js in the Leaflet source. */
  _fireMouseEvent(event) {
    if (!this.hasEventListeners(event.type)) {
      return;
    }

    const map = this._map;
    const containerPoint = map.mouseEventToContainerPoint(event);
    const layerPoint = map.containerPointToLayerPoint(containerPoint);
    const latlng = map.layerPointToLatLng(layerPoint);

    this.fire(event.type, {
      latlng,
      layerPoint,
      containerPoint,
      originalEvent: event,
    });
  },

  _updateCorner(corner, latlng) {
    this._corners[corner] = latlng;
    this._reset();
  },

  /* Copied from Leaflet v0.7 https://github.com/Leaflet/Leaflet/blob/66282f14bcb180ec87d9818d9f3c9f75afd01b30/src/dom/DomUtil.js#L189-L199 */
  /* since L.DomUtil.getTranslateString() is deprecated in Leaflet v1.0 */
  _getTranslateString(point) {
    // on WebKit browsers (Chrome/Safari/iOS Safari/Android) using translate3d instead of translate
    // makes animation smoother as it ensures HW accel is used. Firefox 13 doesn't care
    // (same speed either way), Opera 12 doesn't support translate3d

    const is3d = L.Browser.webkit3d;
    const open = `translate${is3d ? "3d" : ""}(`;
    const close = `${is3d ? ",0" : ""})`;

    return `${open + point.x}px,${point.y}px${close}`;
  },

  _reset() {
    const map = this._map;
    if (map) {
      const image = this._image;
      const latLngToLayerPoint = L.bind(map.latLngToLayerPoint, map);

      const transformMatrix =
        this._calculateProjectiveTransform(latLngToLayerPoint);
      const topLeft = latLngToLayerPoint(this._corners[0]);

      const warp = L.DomUtil.getMatrixString(transformMatrix);
      const translation = this._getTranslateString(topLeft);

      /* See L.DomUtil.setPosition. Mainly for the purposes of L.Draggable. */
      image._leaflet_pos = topLeft;

      image.style[L.DomUtil.TRANSFORM] = [translation, warp].join(" ");

      /* Set origin to the upper-left corner rather than the center of the image, which is the default. */
      image.style[`${L.DomUtil.TRANSFORM}-origin`] = "0 0 0";
    }
  },

  /*
   * Calculates the transform string that will be correct *at the end* of zooming.
   * Leaflet then generates a CSS3 animation between the current transform and
   *		 future transform which makes the transition appear smooth.
   */
  _animateZoom(event) {
    const map = this._map;
    if (map) {
      const image = this._image;
      const latLngToNewLayerPoint = function (latlng) {
        return map._latLngToNewLayerPoint(latlng, event.zoom, event.center);
      };

      const transformMatrix = this._calculateProjectiveTransform(
        latLngToNewLayerPoint
      );
      const topLeft = latLngToNewLayerPoint(this._corners[0]);

      const warp = L.DomUtil.getMatrixString(transformMatrix);
      const translation = this._getTranslateString(topLeft);

      /* See L.DomUtil.setPosition. Mainly for the purposes of L.Draggable. */
      image._leaflet_pos = topLeft;

      if (!L.Browser.gecko) {
        image.style[L.DomUtil.TRANSFORM] = [translation, warp].join(" ");
      }
    }
  },

  getCorners() {
    return this._corners;
  },

  /*
   * Calculates the centroid of the image.
   *		 See http://stackoverflow.com/questions/6149175/logical-question-given-corners-find-center-of-quadrilateral
   */
  getCenter(ll2c, c2ll) {
    const map = this._map;
    if (map) {
      const latLngToCartesian = ll2c || map.latLngToLayerPoint;
      const cartesianToLatLng = c2ll || map.layerPointToLatLng;
      const nw = latLngToCartesian.call(map, this._corners[0]);
      const ne = latLngToCartesian.call(map, this._corners[1]);
      const se = latLngToCartesian.call(map, this._corners[2]);
      const sw = latLngToCartesian.call(map, this._corners[3]);

      const nmid = nw.add(ne.subtract(nw).divideBy(2));
      const smid = sw.add(se.subtract(sw).divideBy(2));

      return cartesianToLatLng.call(
        map,
        nmid.add(smid.subtract(nmid).divideBy(2))
      );
    }
  },

  _calculateProjectiveTransform(latLngToCartesian) {
    /* Setting reasonable but made-up image defaults
     * allow us to place images on the map before
     * they've finished downloading. */
    const offset = latLngToCartesian(this._corners[0]);
    const w = this._image.offsetWidth || 500;
    const h = this._image.offsetHeight || 375;
    const c = [];
    let j;
    /* Convert corners to container points (i.e. cartesian coordinates). */
    for (j = 0; j < this._corners.length; j++) {
      c.push(latLngToCartesian(this._corners[j])._subtract(offset));
    }

    /*
     * This matrix describes the action of the CSS transform on each corner of the image.
     * It Maps from the coordinate system centered at the upper left corner of the image
     *		 to the region bounded by the latlngs in this._corners.
     * For example:
     *		 0, 0, c[0].x, c[0].y
     *		 says that the upper-left corner of the image Maps to the first latlng in this._corners.
     */
    return L.MatrixUtil.general2DProjection(
      0,
      0,
      c[0].x,
      c[0].y,
      w,
      0,
      c[1].x,
      c[1].y,
      0,
      h,
      c[2].x,
      c[2].y,
      w,
      h,
      c[3].x,
      c[3].y
    );
  },
});

L.DistortableImage = L.DistortableImage || {};

L.DistortableImage.Edit = L.Handler.extend({
  options: {
    opacity: 0.7,
    outline: "1px solid red",
    keymap: {
      68: "_toggleRotateDistort", // d
      73: "_toggleIsolate", // i
      76: "_toggleLock", // l
      79: "_toggleOutline", // o
      82: "_toggleRotateDistort", // r
      84: "_toggleTransparency", // t
    },
  },

  initialize(overlay) {
    this._overlay = overlay;

    /* Interaction modes. */
    this._mode = this._overlay.options.mode || "distort";
    this._transparent = false;
    this._outlined = false;
  },

  /* Run on image seletion. */
  addHooks() {
    const overlay = this._overlay;
    const map = overlay._map;
    let i;

    this._lockHandles = new L.LayerGroup();
    for (i = 0; i < 4; i++) {
      this._lockHandles.addLayer(
        new L.LockHandle(overlay, i, { draggable: false })
      );
    }

    this._distortHandles = new L.LayerGroup();
    for (i = 0; i < 4; i++) {
      this._distortHandles.addLayer(new L.DistortHandle(overlay, i));
    }

    this._rotateHandles = new L.LayerGroup();
    for (i = 0; i < 4; i++) {
      this._rotateHandles.addLayer(new L.RotateHandle(overlay, i));
    }

    this._scaleHandles = new L.LayerGroup();

    const topLeftScaleHandle = new L.ScaleHandle(overlay, 0);

    // topLeftScaleHandle.options.icon.style[L.DomUtil.TRANSFORM] += ' rotateZ(90deg)';
    this._scaleHandles.addLayer(topLeftScaleHandle);

    // this._scaleHandles.addLayer( new L.ScaleHandle(overlay, 0));
    this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 1));
    this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 2));
    this._scaleHandles.addLayer(new L.ScaleHandle(overlay, 3));

    this._handles = {
      lock: this._lockHandles,
      distort: this._distortHandles,
      rotate: this._rotateHandles,
      scale: this._scaleHandles,
    };

    if (this._mode === "lock") {
      map.addLayer(this._lockHandles);
    } else {
      this._mode = "distort";
      map.addLayer(this._distortHandles);
      this._enableDragging();
    }

    // overlay.on('click', this._showToolbar, this);
    L.DomEvent.on(overlay._image, "click", this._showToolbar, this);

    /* Enable hotkeys. */
    L.DomEvent.on(window, "keydown", this._onKeyDown, this);

    overlay.fire("select");
  },

  /* Run on image deseletion. */
  removeHooks() {
    const overlay = this._overlay;
    const map = overlay._map;

    // L.DomEvent.off(window, 'keydown', this._onKeyDown, this);

    L.DomEvent.off(overlay._image, "click", this._showToolbar, this);

    // First, check if dragging exists;
    // it may be off due to locking
    if (this.dragging) {
      this.dragging.disable();
    }
    delete this.dragging;

    map.removeLayer(this._handles[this._mode]);

    /* Disable hotkeys. */
    L.DomEvent.off(window, "keydown", this._onKeyDown, this);

    overlay.fire("deselect");
  },

  _rotateBy(angle) {
    const overlay = this._overlay;
    const map = overlay._map;
    const center = map.latLngToLayerPoint(overlay.getCenter());
    let i;
    let p;
    let q;

    for (i = 0; i < 4; i++) {
      p = map.latLngToLayerPoint(overlay._corners[i]).subtract(center);
      q = new L.Point(
        Math.cos(angle) * p.x - Math.sin(angle) * p.y,
        Math.sin(angle) * p.x + Math.cos(angle) * p.y
      );
      overlay._corners[i] = map.layerPointToLatLng(q.add(center));
    }

    overlay._reset();
  },

  _scaleBy(scale) {
    const overlay = this._overlay;
    const map = overlay._map;
    const center = map.latLngToLayerPoint(overlay.getCenter());
    let i;
    let p;

    for (i = 0; i < 4; i++) {
      p = map
        .latLngToLayerPoint(overlay._corners[i])
        .subtract(center)
        .multiplyBy(scale)
        .add(center);
      overlay._corners[i] = map.layerPointToLatLng(p);
    }

    overlay._reset();
  },

  _enableDragging() {
    const overlay = this._overlay;
    const map = overlay._map;

    this.dragging = new L.Draggable(overlay._image);
    this.dragging.enable();

    /* Hide toolbars while dragging; click will re-show it */
    this.dragging.on("dragstart", this._hideToolbar, this);

    /*
     * Adjust default behavior of L.Draggable.
     * By default, L.Draggable overwrites the CSS3 distort transform
     * that we want when it calls L.DomUtil.setPosition.
     */
    this.dragging._updatePosition = function () {
      const delta = this._newPos.subtract(
        map.latLngToLayerPoint(overlay._corners[0])
      );
      let currentPoint;
      let i;

      this.fire("predrag");

      for (i = 0; i < 4; i++) {
        currentPoint = map.latLngToLayerPoint(overlay._corners[i]);
        overlay._corners[i] = map.layerPointToLatLng(currentPoint.add(delta));
      }
      overlay._reset();
      overlay.fire("update");

      this.fire("drag");
    };
  },

  _onKeyDown(event) {
    const { keymap } = this.options;
    const handlerName = keymap[event.which];

    if (handlerName !== undefined) {
      this[handlerName].call(this);
    }
  },

  _toggleRotateDistort() {
    const map = this._overlay._map;

    map.removeLayer(this._handles[this._mode]);

    /* Switch mode. */
    if (this._mode === "rotate") {
      this._mode = "distort";
    } else {
      this._mode = "rotate";
    }

    map.addLayer(this._handles[this._mode]);
  },

  _toggleScale() {
    const map = this._overlay._map;

    map.removeLayer(this._handles[this._mode]);

    if (this._mode === "scale") {
      this._mode = "distort";
    } else {
      this._mode = "scale";
    }

    map.addLayer(this._handles[this._mode]);
  },

  _toggleTransparency() {
    const image = this._overlay._image;
    let opacity;

    this._transparent = !this._transparent;
    opacity = this._transparent ? this.options.opacity : 1;

    L.DomUtil.setOpacity(image, opacity);
    image.setAttribute("opacity", opacity);
  },

  _toggleOutline() {
    const image = this._overlay._image;
    let opacity;
    let outline;

    this._outlined = !this._outlined;
    opacity = this._outlined ? this.options.opacity / 2 : 1;
    outline = this._outlined ? this.options.outline : "none";

    L.DomUtil.setOpacity(image, opacity);
    image.setAttribute("opacity", opacity);

    image.style.outline = outline;
  },

  _toggleLock() {
    const map = this._overlay._map;

    map.removeLayer(this._handles[this._mode]);
    /* Switch mode. */
    if (this._mode === "lock") {
      this._mode = "distort";
      this._enableDragging();
    } else {
      this._mode = "lock";
      if (this.dragging) {
        this.dragging.disable();
      }
      delete this.dragging;
    }

    map.addLayer(this._handles[this._mode]);
  },

  _hideToolbar() {
    const map = this._overlay._map;
    if (this.toolbar) {
      map.removeLayer(this.toolbar);
      this.toolbar = false;
    }
  },

  _showToolbar(event) {
    const overlay = this._overlay;
    const { target } = event;
    const map = overlay._map;

    /* Ensure that there is only ever one toolbar attached to each image. */
    this._hideToolbar();
    let point;
    if (event.containerPoint) {
      point = event.containerPoint;
    } else {
      point = target._leaflet_pos;
    }
    const raised_point = map.containerPointToLatLng(
      new L.Point(point.x, point.y - 20)
    );
    raised_point.lng = overlay.getCenter().lng;
    this.toolbar = new L.DistortableImage.EditToolbar(raised_point).addTo(
      map,
      overlay
    );
    overlay.fire("toolbar:created");

    L.DomEvent.stopPropagation(event);
  },

  toggleIsolate() {
    // this.isolated = !this.isolated;
    // if (this.isolated) {
    // 	$.each($L.images,function(i,img) {
    // 		img.hidden = false;
    // 		img.setOpacity(1);
    // 	});
    // } else {
    // 	$.each($L.images,function(i,img) {
    // 		img.hidden = true;
    // 		img.setOpacity(0);
    // 	});
    // }
    // this.hidden = false;
    // this.setOpacity(1);
  },
});

L.DistortableImageOverlay.addInitHook(function () {
  this.editing = new L.DistortableImage.Edit(this);

  if (this.options.editable) {
    L.DomEvent.on(this._image, "load", this.editing.enable, this.editing);
  }

  this.on("remove", function () {
    if (this.editing) {
      this.editing.disable();
    }
  });
});
